import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Users, Search, ChevronLeft, Trash2, Clock, CreditCard, MoreVertical, UserCheck, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setLoading(true);
    axios.get(`${API}/admin/users`, { headers })
      .then(res => {
        setUsers(res.data.users);
        setTotal(res.data.total);
      })
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const extendTrial = async (userId, days) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(`${API}/admin/users/${userId}`, { extend_trial_days: days }, { headers });
      toast.success(`Prøveperiode forlænget med ${days} dage`);
      fetchUsers();
    } catch {
      toast.error("Fejl ved forlængelse");
    }
  };

  const toggleSubscription = async (userId, active) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(`${API}/admin/users/${userId}`, { subscription_active: active }, { headers });
      toast.success(active ? "Abonnement aktiveret" : "Abonnement deaktiveret");
      fetchUsers();
    } catch {
      toast.error("Fejl ved opdatering");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Er du sikker på du vil slette denne bruger? Dette kan ikke fortrydes.")) return;
    
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success("Bruger slettet");
      fetchUsers();
    } catch {
      toast.error("Fejl ved sletning");
    }
  };

  const getStatus = (user) => {
    if (user.subscription_active) return { label: "Abonnent", color: "text-emerald-400 bg-emerald-500/10" };
    const trialEnd = new Date(user.trial_ends_at);
    if (trialEnd > new Date()) {
      const daysLeft = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));
      return { label: `Prøve (${daysLeft}d)`, color: "text-[#DFFF00] bg-[#DFFF00]/10" };
    }
    return { label: "Udløbet", color: "text-red-400 bg-red-500/10" };
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#09090b] via-[#0c0c0f] to-[#09090b]">
      {/* Admin Navbar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <div className="w-9 h-9 rounded-lg bg-red-500 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
                Brugere
              </span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-users">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-tight text-white mb-1">
              Bruger Administration
            </h1>
            <p className="text-sm text-zinc-400">{total} brugere i alt</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              placeholder="Søg brugere..."
              className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 pl-10"
            />
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl h-20 animate-pulse" />)}
          </div>
        ) : (
          <div className="bg-[#18181b] border border-white/5 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs uppercase tracking-wider text-zinc-500 px-5 py-4">Bruger</th>
                    <th className="text-left text-xs uppercase tracking-wider text-zinc-500 px-5 py-4">Status</th>
                    <th className="text-left text-xs uppercase tracking-wider text-zinc-500 px-5 py-4">Oprettet</th>
                    <th className="text-left text-xs uppercase tracking-wider text-zinc-500 px-5 py-4">Prøve Udløber</th>
                    <th className="text-right text-xs uppercase tracking-wider text-zinc-500 px-5 py-4">Handlinger</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const status = getStatus(user);
                    return (
                      <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`user-row-${user.id}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 flex items-center justify-center">
                              <span className="text-sm font-bold text-[#DFFF00]">{user.name?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-white">{user.name}</p>
                              <p className="text-xs text-zinc-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-zinc-400">
                            {new Date(user.created_at).toLocaleDateString("da-DK")}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-zinc-400">
                            {new Date(user.trial_ends_at).toLocaleDateString("da-DK")}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4 text-zinc-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                              <DropdownMenuItem onClick={() => extendTrial(user.id, 7)} className="text-zinc-300 focus:text-white focus:bg-white/5">
                                <Clock className="h-4 w-4 mr-2" /> +7 dage prøve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => extendTrial(user.id, 30)} className="text-zinc-300 focus:text-white focus:bg-white/5">
                                <Clock className="h-4 w-4 mr-2" /> +30 dage prøve
                              </DropdownMenuItem>
                              {user.subscription_active ? (
                                <DropdownMenuItem onClick={() => toggleSubscription(user.id, false)} className="text-orange-400 focus:text-orange-300 focus:bg-orange-500/10">
                                  <UserX className="h-4 w-4 mr-2" /> Deaktiver abonnement
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => toggleSubscription(user.id, true)} className="text-emerald-400 focus:text-emerald-300 focus:bg-emerald-500/10">
                                  <UserCheck className="h-4 w-4 mr-2" /> Aktiver abonnement
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => deleteUser(user.id)} className="text-red-400 focus:text-red-300 focus:bg-red-500/10">
                                <Trash2 className="h-4 w-4 mr-2" /> Slet bruger
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Ingen brugere fundet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
